import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyCaption, TypographyH1 } from '@heidi/design-system/typography';
import { formatWithMomentToken } from '@heidi/utils/dates';
import { isValid, parseISO } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import type { DetailedPublicTemplateResponse } from '../../../../contracts';
import { communityTemplateDetailsMessages } from '../../communityTemplateDetails.messages';

interface DetailsHeaderProps {
  dateFormat: string;
  isLoading: boolean;
  template: DetailedPublicTemplateResponse | undefined;
  getSpecialtyDisplayName: (specialty: string) => string;
}

export const DetailsHeader = ({
  dateFormat,
  isLoading,
  template,
  getSpecialtyDisplayName,
}: DetailsHeaderProps) => {
  const intl = useIntl();

  return (
    <div className="flex w-full flex-col gap-6">
      {isLoading ? (
        <Skeleton className="h-12 w-full max-w-sm" />
      ) : (
        <TypographyH1 data-testid="community-template-details-title">
          {template?.title}
        </TypographyH1>
      )}
      <div className="grid grid-cols-2 justify-between gap-4 lg:grid-cols-4">
        <div className="flex flex-col">
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage {...communityTemplateDetailsMessages.createdByLabel} />
          </TypographyCaption>
          {isLoading ? (
            <Skeleton className="h-4 w-full max-w-40" />
          ) : (
            <TypographyCaption>
              {template?.author_name.replaceAll(/\s/g, '') === '' ? (
                <FormattedMessage {...communityTemplateDetailsMessages.anonymousAuthor} />
              ) : (
                template?.author_name
              )}
            </TypographyCaption>
          )}
        </div>
        <div className="flex flex-col">
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage {...communityTemplateDetailsMessages.specialtyLabel} />
          </TypographyCaption>
          {isLoading ? (
            <Skeleton className="h-4 w-full max-w-40" />
          ) : (
            <TypographyCaption>
              {getSpecialtyDisplayName(template?.specialty ?? '')}
            </TypographyCaption>
          )}
        </div>
        <div className="flex flex-col">
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage {...communityTemplateDetailsMessages.lastEditedLabel} />
          </TypographyCaption>
          {isLoading ? (
            <Skeleton className="h-4 w-full max-w-40" />
          ) : (
            <TypographyCaption>
              {(() => {
                if (!template) return '';
                // Guard against malformed BE strings — date-fns `format`
                // throws on Invalid Date, unlike moment's softer "Invalid date".
                const parsed = parseISO(template.updated_at);
                return isValid(parsed) ? formatWithMomentToken(parsed, dateFormat) : '';
              })()}
            </TypographyCaption>
          )}
        </div>
        <div className="flex flex-col">
          <TypographyCaption className="text-text-secondary">
            <FormattedMessage {...communityTemplateDetailsMessages.usesLabel} />
          </TypographyCaption>
          {isLoading ? (
            <Skeleton className="h-4 w-full max-w-40" />
          ) : (
            <TypographyCaption>
              {intl.formatMessage(communityTemplateDetailsMessages.importsCount, {
                count: template?.imports_count ?? 0,
              })}
            </TypographyCaption>
          )}
        </div>
      </div>
    </div>
  );
};
